import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import Moment from "moment";

import { CustomersContext } from "../../context/customersContext";
import { SalesContext } from "../../context/salesContext";
import { SALES_PAGE_SIZE_OPTIONS } from "../../constants/config";
import { getStatusTone } from "../../constants/saleStatus";
import { Badge, DataTable, SearchBar } from "../ui";

const renderPendingDays = (row) => {
    if (!row.expectedCompletionDate) return null;
    const status = (row.workOrder && row.workOrder.status) || "";
    if (status === "Completed") return null;

    const expected = Moment(row.expectedCompletionDate).utc().startOf("day");
    const today = Moment().utc().startOf("day");
    const days = expected.diff(today, "days");

    if (days < 0) {
        return (
            <span className="text-danger-600">
                {Math.abs(days)} day{Math.abs(days) === 1 ? "" : "s"} overdue
            </span>
        );
    }
    if (days === 0) {
        return <span className="text-info-600">Due today</span>;
    }
    if (days < 4) {
        return (
            <span className="text-warning-700">
                {days} day{days === 1 ? "" : "s"} left
            </span>
        );
    }
    return (
        <span className="text-ink-500">
            {days} day{days === 1 ? "" : "s"}
        </span>
    );
};

const pendingDaysSortValue = (row) => {
    if (!row.expectedCompletionDate) return Number.POSITIVE_INFINITY;
    const expected = Moment(row.expectedCompletionDate).utc().startOf("day");
    const today = Moment().utc().startOf("day");
    return expected.diff(today, "days");
};

const computeBalance = (row) => {
    const paid = (row.payments || []).reduce(
        (acc, payment) => acc + Number(payment.amount || 0),
        0,
    );
    const price = Number(row.salesPrice || 0);
    return Number((price - paid).toFixed(2));
};

const SalesTable = ({
    salesList,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    loading,
    openCustomerModal,
    openSaleModal,
    onRefresh,
    filters = [],
    emptyMessage = "No sales to display.",
    search = "",
    onSearchChange,
    onSortChange,
    defaultSort,
}) => {
    const { action } = useContext(SalesContext);
    const { customers } = useContext(CustomersContext);

    const customerById = useMemo(() => {
        const map = new Map();
        (customers || []).forEach((c) => map.set(c._id, c));
        return map;
    }, [customers]);

    const columns = useMemo(
        () => [
            {
                id: "orderDate",
                label: "Ordered",
                sortable: true,
                serverSortField: "orderDate",
                cell: (row) =>
                    row.orderDate ? Moment(row.orderDate).utc().format("MMM D, YYYY") : null,
            },
            {
                id: "customerId",
                label: "Customer",
                sortable: true,
                serverSortField: "customerId",
                sortValue: (row) => {
                    const c = customerById.get(row.customerId);
                    return c ? `${c.firstName || ""} ${c.lastName || ""}` : row.customerId;
                },
                cell: (row) => {
                    const customer = customerById.get(row.customerId);
                    if (customer && customer.firstName) {
                        return (
                            <span className="text-ink-900">
                                {customer.firstName} {customer.lastName || ""}
                            </span>
                        );
                    }
                    return <span className="text-ink-400">{row.customerId}</span>;
                },
            },
            {
                id: "po",
                label: "PO",
                sortable: true,
                serverSortField: "po",
                cellClassName: "",
            },
            {
                id: "product",
                label: "Product",
                sortable: true,
                serverSortField: "product",
                hideBelow: "lg",
            },
            {
                id: "salesPrice",
                label: "Price",
                sortable: true,
                serverSortField: "salesPrice",
                align: "right",
                hideBelow: "md",
                cellClassName: "tabular-nums",
                cell: (row) =>
                    row.salesPrice != null ? `$${Number(row.salesPrice).toFixed(2)}` : null,
            },
            {
                id: "balance",
                label: "Balance",
                sortable: true,
                serverSortField: "balance",
                align: "right",
                hideBelow: "md",
                cellClassName: "tabular-nums",
                sortValue: computeBalance,
                cell: (row) => {
                    const balance = computeBalance(row);
                    return balance > 0 ? (
                        <span className="text-danger-600">${balance.toFixed(2)}</span>
                    ) : (
                        <span className="text-ink-400">—</span>
                    );
                },
            },
            {
                id: "expectedCompletionDate",
                label: "Expected",
                sortable: true,
                serverSortField: "expectedCompletionDate",
                hideBelow: "lg",
                cell: (row) =>
                    row.expectedCompletionDate
                        ? Moment(row.expectedCompletionDate).utc().format("MMM D, YYYY")
                        : null,
            },
            {
                id: "pendingDays",
                label: "Pending",
                sortable: true,
                serverSortField: "pendingDays",
                hideBelow: "md",
                sortValue: pendingDaysSortValue,
                cell: renderPendingDays,
            },
            {
                id: "status",
                label: "Status",
                sortable: true,
                serverSortField: "workOrder.status",
                sortValue: (row) => (row.workOrder && row.workOrder.status) || "",
                cell: (row) => {
                    const status = (row.workOrder && row.workOrder.status) || "";
                    return status ? <Badge tone={getStatusTone(status)}>{status}</Badge> : null;
                },
            },
            {
                id: "updatedDate",
                label: "Updated",
                sortable: true,
                serverSortField: "updatedDate",
                hideBelow: "xl",
                cellClassName: "text-ink-500",
                cell: (row) =>
                    row.updatedDate ? Moment(row.updatedDate).format("MMM D, YYYY, h:mma") : null,
            },
        ],
        [customerById],
    );

    const bulkActions = useMemo(
        () => [
            {
                label: "Clear Balance",
                variant: "success",
                confirmTitle: "Clear balances?",
                confirmDescription:
                    "This will add a payment dated today for the remaining balance on every selected sale.",
                confirmLabel: "Clear",
                confirmVariant: "success",
                successModal: true,
                successTitle: "Balances cleared",
                successMessage:
                    "A payment for the remaining balance was added to every selected sale.",
                errorMessage: "An error occurred while clearing balances. Please contact support.",
                onClick: async (selectedIds) => {
                    const rowsById = new Map(salesList.map((sale) => [sale._id, sale]));
                    const today = Moment().format("YYYY-MM-DD");
                    await Promise.all(
                        selectedIds.map((id) => {
                            const sale = rowsById.get(id);
                            if (!sale) return Promise.resolve();
                            const balance = computeBalance(sale);
                            if (balance <= 0) return Promise.resolve();
                            const payment = {
                                date: today,
                                amount: balance,
                                description: "Balance cleared",
                            };
                            const nextPayments = [...(sale.payments || []), payment];
                            return action.updateSale(id, { ...sale, payments: nextPayments });
                        }),
                    );
                    if (onRefresh) await onRefresh();
                },
            },
            {
                label: "Delete",
                variant: "danger",
                onClick: async (selectedIds) => {
                    await Promise.all(selectedIds.map((id) => action.deleteSale(id)));
                    if (onRefresh) await onRefresh();
                },
                successMessage: "Record(s) successfully deleted",
                errorMessage: "An error occurred while deleting. Please contact support.",
            },
        ],
        [action, salesList, onRefresh],
    );

    return (
        <DataTable
            rows={salesList}
            columns={columns}
            onSortChange={onSortChange}
            topContent={
                <SearchBar
                    id="sales-search-table"
                    value={search}
                    onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                    placeholder="Search PO # or customer name / phone"
                />
            }
            selectable
            defaultSort={defaultSort || { id: "orderDate", direction: "desc" }}
            onRowClick={(row) => openSaleModal(row._id)}
            bulkActions={bulkActions}
            filters={filters}
            loading={loading}
            emptyMessage={emptyMessage}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rowsPerPageOptions={SALES_PAGE_SIZE_OPTIONS}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
        />
    );
};

SalesTable.propTypes = {
    salesList: PropTypes.array.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    openCustomerModal: PropTypes.func.isRequired,
    openSaleModal: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    filters: PropTypes.array,
    emptyMessage: PropTypes.string,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    onSortChange: PropTypes.func,
    defaultSort: PropTypes.shape({
        id: PropTypes.string,
        direction: PropTypes.oneOf(["asc", "desc"]),
    }),
};

export default SalesTable;
